import React, { type CSSProperties } from 'react'
import { Card, CardContent, Typography, Stack, Grid, Tooltip, useTheme } from '@mui/material'
import { green } from '@mui/material/colors'
import clsx from 'clsx'
import { type StrategyTrade } from '../../../models/trade/strategy-trade'
import { replaceExchangeShortname } from '../../../utils/exchange-utils'
import { formatToLocalMilliTimestampFromUTCString } from '../../../utils/time-utils'
import { getProductTypeColour } from '../../../utils/colours-utils'

interface TradeCardProps {
  style: CSSProperties
  data: StrategyTrade
}

const TradeCard: React.FunctionComponent<TradeCardProps> = (props) => {
  const { time, orderId, account, symbol, side, price, size, tag, role } = props.data
  const displayRole = role ?? 'MAKE'

  const theme = useTheme()

  return (
    <Card
      variant='outlined'
      sx={{
        ...props.style,
        background: 'transparent',
        '& .tag.quoter': {
          background: green[900],
        },
        '& .tag.hedger': {
          background: theme.palette.background.paper,
        },
        '& .role.make': {
          border: '8px solid ' + theme.palette.background.paper,
        },
        '& .role.take': {
          border: '8px solid ' + theme.palette.grey[400],
        },
        '& .size.zero': {
          border: '8px dashed ' + theme.palette.grey[400],
        },
      }}
    >
      <CardContent
        className={clsx('tag', 'role', 'size', {
          quoter: tag?.toLowerCase() === 'quote',
          hedger: tag?.toLowerCase() === 'hedge',
          make: displayRole?.toLowerCase() === 'make',
          take: displayRole?.toLowerCase() === 'take',
          zero: size === 0,
          remainder: size < 0,
        })}
      >
        <Stack direction='row' justifyContent='space-between'>
          <Stack
            direction='row'
            gap={1}
          >
            <Tooltip title={symbol} placement='right'>
              <Typography fontWeight='bold' maxWidth='180px' noWrap={true} lineHeight={1} fontSize='12px' sx={{ color: getProductTypeColour(symbol) }}>
                {replaceExchangeShortname(symbol)}
              </Typography>
            </Tooltip>
          </Stack>
          <Stack>
            <Typography variant='caption' align='right' lineHeight={1} fontSize='9px'>
              {formatToLocalMilliTimestampFromUTCString(time)}
            </Typography>
            <Tooltip title={account} placement='left'>
              <Typography variant='caption' maxWidth='180px' align='right' lineHeight={1} noWrap={true} fontSize='9px'>
                {account}
              </Typography>
            </Tooltip>
            <Tooltip title={orderId} placement='left'>
              <Typography variant='caption' maxWidth='180px' align='right' lineHeight={1} noWrap={true} fontSize='9px'>
                {orderId}
              </Typography>
            </Tooltip>
          </Stack>
        </Stack>
        <Grid container alignItems='baseline'>
          <Grid
            item
            xs={2}
            sx={{
              '& .text.buy': {
                color: 'success.light'
              },
              '& .text.sell': {
                color: 'error.light'
              }
            }}
          >
            <Tooltip title={`${displayRole ?? '[no-tag]'} ${tag ?? '[no-role]'}`} placement='right'>
              <Typography
                fontSize='large'
                className={clsx('text', {
                  buy: side?.toLowerCase() === 'buy',
                  sell: side?.toLowerCase() === 'sell'
                })}
              >
                {side.toUpperCase()}
              </Typography>
            </Tooltip>
          </Grid>
          <Grid item xs={5}>
            <Tooltip title={price} placement='right'>
              <Typography fontSize='18px' lineHeight={1} width='145px' noWrap textOverflow='ellipsis'>
                {price}
              </Typography>
            </Tooltip>
          </Grid>
          <Grid item xs={5}>
            <Tooltip title={size} placement='right'>
              <Typography fontSize='18px' lineHeight={1} width='145px' noWrap textOverflow='ellipsis'>
                {size}
              </Typography>
            </Tooltip>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default TradeCard
